import React, { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { CreateProvaData, ProvaComEstatisticas } from '../../../types/provas';

interface EditProvaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateProvaData) => void;
  loading: boolean;
  prova: ProvaComEstatisticas;
}

export const EditProvaModal: React.FC<EditProvaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  loading,
  prova
}) => {
  const [formData, setFormData] = useState<CreateProvaData>({
    id_grupo: prova.id_grupo,
    titulo: prova.titulo,
    descricao: prova.descricao || '',
    total_questoes_prova: prova.total_questoes_prova,
    pontuacao_maxima: prova.pontuacao_maxima,
    tempo_limite_minutos: prova.tempo_limite_minutos,
    permitir_revisao: prova.permitir_revisao,
  });

  // Atualizar dados quando a prova mudar
  useEffect(() => {
    if (prova) {
      setFormData({
        id_grupo: prova.id_grupo,
        titulo: prova.titulo,
        descricao: prova.descricao || '',
        total_questoes_prova: prova.total_questoes_prova,
        pontuacao_maxima: prova.pontuacao_maxima,
        tempo_limite_minutos: prova.tempo_limite_minutos,
        permitir_revisao: prova.permitir_revisao,
      });
    }
  }, [prova]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      enqueueSnackbar('O título da prova é obrigatório', { variant: 'error' });
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
      <div className="bg-card rounded-lg shadow-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Editar Prova</h2>
          <Button variant="outline" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="titulo" className="text-sm font-medium text-foreground">
              Título da Prova *
            </Label>
            <input
              id="titulo"
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              placeholder="Digite o título da prova"
              required
            />
          </div>

          <div>
            <Label htmlFor="descricao" className="text-sm font-medium text-foreground">
              Descrição
            </Label>
            <textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring resize-none"
              rows={3}
              placeholder="Descrição opcional da prova"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="total_questoes" className="text-sm font-medium text-foreground">
                Número de Questões na Prova *
              </Label>
              <input
                id="total_questoes"
                type="number"
                min="1"
                max="100"
                value={formData.total_questoes_prova}
                onChange={(e) => setFormData({ ...formData, total_questoes_prova: parseInt(e.target.value) || 1 })}
                className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Quantas questões aparecerão na prova para cada militar
              </p>
            </div>

            <div>
              <Label htmlFor="pontuacao_maxima" className="text-sm font-medium text-foreground">
                Pontuação Máxima *
              </Label>
              <input
                id="pontuacao_maxima"
                type="number"
                min="1"
                step="0.1"
                value={formData.pontuacao_maxima}
                onChange={(e) => setFormData({ ...formData, pontuacao_maxima: parseFloat(e.target.value) || 1 })}
                className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="tempo_limite" className="text-sm font-medium text-foreground">
              Tempo Limite (minutos)
            </Label>
            <input
              id="tempo_limite"
              type="number"
              min="1"
              value={formData.tempo_limite_minutos || ''}
              onChange={(e) => setFormData({ ...formData, tempo_limite_minutos: e.target.value ? parseInt(e.target.value) : undefined })}
              className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              placeholder="Deixe vazio para sem limite de tempo"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Deixe vazio se não quiser definir limite de tempo
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              id="permitir_revisao"
              type="checkbox"
              checked={formData.permitir_revisao}
              onChange={(e) => setFormData({ ...formData, permitir_revisao: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-ring border-border rounded"
            />
            <Label htmlFor="permitir_revisao" className="text-sm font-medium text-foreground">
              Permitir revisão das respostas antes de finalizar
            </Label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
